import { useCallback, useEffect, useRef } from "react"
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore"
import { useInfiniteQuery, useQueryClient, type QueryKey } from "@tanstack/react-query"
import { db } from "../firebaseFirestore"

const DEFAULT_PAGE_SIZE = 10
// If a fetched page is entirely filtered out by transformPage (e.g. every doc on the page fails
// a client-side visibility check), auto-fetch the next page instead of leaving the scroll
// sentinel stuck with nothing new to show. Capped so a run of empty/near-empty pages can't turn
// one scroll trigger into an unbounded read burst on a slow connection.
const MAX_CONSECUTIVE_AUTO_CONTINUE = 3

export type RawDoc = { id: string } & DocumentData

interface PageResult<T> {
  items: T[]
  rawDocs: RawDoc[]
  lastRawDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}

export interface UsePaginatedFirestoreListOptions<T> {
  /** react-query cache key; include every value the query constraints depend on. */
  queryKey: QueryKey
  collectionPath: string
  /** where()/orderBy() constraints, excluding the pagination orderBy field/limit/startAfter. */
  queryConstraints: QueryConstraint[]
  /** Stable field to order and paginate by (must be present on every doc). */
  orderByField: string
  pageSize?: number
  /** Client-side filter/decorate step, scoped to a single page's raw docs. */
  transformPage: (rawDocs: RawDoc[]) => Promise<T[]>
  /** Keep the first page live via a bounded onSnapshot listener. Defaults to true. */
  liveFirstPage?: boolean
  enabled?: boolean
}

export interface UsePaginatedFirestoreListResult<T> {
  items: T[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: unknown
  fetchNextPage: () => void
  refresh: () => void
  /**
   * Local, optimistic update of the flattened item list (e.g. a like toggle). The updater must
   * preserve array length/order — items are redistributed back into their original pages by
   * position, not re-fetched.
   */
  setItems: (update: T[] | ((current: T[]) => T[])) => void
}

export function usePaginatedFirestoreList<T extends { id: string }>({
  queryKey,
  collectionPath,
  queryConstraints,
  orderByField,
  pageSize = DEFAULT_PAGE_SIZE,
  transformPage,
  liveFirstPage = true,
  enabled = true,
}: UsePaginatedFirestoreListOptions<T>): UsePaginatedFirestoreListResult<T> {
  const queryClient = useQueryClient()
  const transformPageRef = useRef(transformPage)
  transformPageRef.current = transformPage

  const fetchPage = async (
    cursor: QueryDocumentSnapshot<DocumentData> | null,
  ): Promise<PageResult<T>> => {
    let remainingAutoContinues = MAX_CONSECUTIVE_AUTO_CONTINUE
    let nextCursor = cursor

    while (true) {
      const constraints = [
        ...queryConstraints,
        orderBy(orderByField),
        ...(nextCursor ? [startAfter(nextCursor)] : []),
        limit(pageSize),
      ]
      const snap = await getDocs(query(collection(db, collectionPath), ...constraints))
      const rawDocs: RawDoc[] = []
      snap.forEach((doc) => rawDocs.push({ id: doc.id, ...doc.data() }))
      const lastRawDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
      const hasMore = rawDocs.length === pageSize
      const items = await transformPageRef.current(rawDocs)

      if (items.length > 0 || !hasMore || remainingAutoContinues <= 0) {
        return { items, rawDocs, lastRawDoc, hasMore }
      }

      remainingAutoContinues -= 1
      nextCursor = lastRawDoc
    }
  }

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: null as QueryDocumentSnapshot<DocumentData> | null,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastRawDoc : undefined),
  })

  // queryKey is commonly passed as a fresh array literal by callers on every render (see
  // RaffleContext), and infiniteQuery is a brand-new object every render regardless. Reading both
  // through refs lets fetchNextPage/refresh/setItems below keep a stable identity across renders,
  // so consumers that depend on them in a useEffect (e.g. `useEffect(() => refresh(), [refresh])`)
  // don't re-run on every render and trigger an infinite refetch -> re-render loop (React #185).
  const queryKeyRef = useRef(queryKey)
  queryKeyRef.current = queryKey
  const infiniteQueryRef = useRef(infiniteQuery)
  infiniteQueryRef.current = infiniteQuery

  const fetchNextPage = useCallback(() => {
    const current = infiniteQueryRef.current
    if (current.hasNextPage && !current.isFetchingNextPage) {
      void current.fetchNextPage()
    }
  }, [])

  const refresh = useCallback(() => {
    void queryClient.resetQueries({ queryKey: queryKeyRef.current })
  }, [queryClient])

  const setItems = useCallback(
    (update: T[] | ((current: T[]) => T[])) => {
      queryClient.setQueryData(queryKeyRef.current, (current: any) => {
        if (!current) {
          return current
        }
        const currentItems: T[] = current.pages.flatMap((page: PageResult<T>) => page.items)
        const nextItems = typeof update === "function" ? (update as (c: T[]) => T[])(currentItems) : update
        let cursor = 0
        const pages = current.pages.map((page: PageResult<T>) => {
          const count = page.items.length
          const slice = nextItems.slice(cursor, cursor + count)
          cursor += count
          return { ...page, items: slice }
        })
        return { ...current, pages }
      })
    },
    [queryClient],
  )

  // Keep the first page live: a bounded listener on the same constraints re-runs transformPage
  // and swaps only pages[0] into the cache, so scrolled-in pages are never re-fetched by this.
  useEffect(() => {
    if (!liveFirstPage || !enabled) {
      return undefined
    }

    const schedule =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (window as any).requestIdleCallback
        : (callback: () => void) => window.setTimeout(callback, 1500)
    const cancel =
      typeof window !== "undefined" && "cancelIdleCallback" in window
        ? (window as any).cancelIdleCallback
        : globalThis.clearTimeout

    let cancelled = false
    let unsubscribe: (() => void) | undefined

    const scheduleId = schedule(() => {
      if (cancelled) {
        return
      }

      const firstPageQuery = query(
        collection(db, collectionPath),
        ...queryConstraints,
        orderBy(orderByField),
        limit(pageSize),
      )

      unsubscribe = onSnapshot(firstPageQuery, async (snap) => {
        if (cancelled) {
          return
        }

        const rawDocs: RawDoc[] = []
        snap.forEach((doc) => rawDocs.push({ id: doc.id, ...doc.data() }))
        const lastRawDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
        const hasMore = rawDocs.length === pageSize
        const items = await transformPageRef.current(rawDocs)

        if (cancelled) {
          return
        }

        const nextFirstPage: PageResult<T> =
          items.length === 0 && hasMore
            ? await fetchPage(null)
            : { items, rawDocs, lastRawDoc, hasMore }

        if (cancelled) {
          return
        }

        queryClient.setQueryData(queryKeyRef.current, (current: any) => {
          if (!current) {
            return current
          }
          const pages = [...current.pages]
          pages[0] = nextFirstPage
          return { ...current, pages }
        })
      })
    })

    return () => {
      cancelled = true
      cancel(scheduleId)
      unsubscribe?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveFirstPage, enabled, collectionPath, orderByField, pageSize, JSON.stringify(queryKey)])

  const items = infiniteQuery.data?.pages.flatMap((page) => page.items) ?? []
  const dedupedItems = Array.from(new Map(items.map((item) => [item.id, item])).values())

  return {
    items: dedupedItems,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: Boolean(infiniteQuery.hasNextPage),
    error: infiniteQuery.error,
    fetchNextPage,
    refresh,
    setItems,
  }
}
